import { StandardPlot } from "../core/objects/svg/standard-plot";
import { OnlineDataPlotObjectGroup } from "../online/online-data-plot-object-group";
import { OfflineDataPlotObjectGroup } from "../offline/offline-data-plot-object-group";

export class UniversalDataPlot extends StandardPlot {
  constructor(settings) {
    super(settings);
    this.onBufferSizeChanged = this._onSignalBoxBufferSizeChanged.bind(this);
    this.signalMode = "online"; // online, offline
  }

  init() {
    this.onlinePlotObjects = new OnlineDataPlotObjectGroup(this);
    this.offlinePlotObjects = new OfflineDataPlotObjectGroup(this);
    super.init();
    this.offlinePlotObjects.initCursorAndMeasurer();
    this.offlinePlotObjects.cursor.setVisibility(false);
    this.offlinePlotObjects.startUpdateMeanValues();
  }

  setCursorOrMeasurerPosition(x) {
    this.offlinePlotObjects.setCursorOrMeasurerPosition(x);
  }

  setOnSelectChannel(action) {
    this.onSelectChannel = (dataSourceId, channelId) => {
      this.offlinePlotObjects.updateCursorAndMeasurerPosition(false);
      action(dataSourceId, channelId);
    };
  }

  afterSignalObjectCreate(signalObject, dataSourceId, channelId, signalId) {
    if (signalObject.setIsolineChangeAction !== undefined) {
      signalObject.setIsolineChangeAction(
        function (v) {
          if (this.signalMode == "online") {
            this.updateMeanValueText(dataSourceId, channelId, signalId, v);
          }
        }.bind(this)
      );
    }
  }

  afterSignalsCreate(dataSourceId, channelId) {
    super.afterSignalsCreate(dataSourceId, channelId);
    this.onlinePlotObjects.createChannelLeadRay();
  }

  setOnBufferSizeChanged(action) {
    this.onBufferSizeChanged = () => {
      this._onSignalBoxBufferSizeChanged();
      action();
    };
  }

  setOnCursorPositionChanged(action) {
    this.offlinePlotObjects.setOnCursorPositionChanged(action);
  }

  setOnMeasurerPositionChanged(action) {
    this.offlinePlotObjects.setOnMeasurerPositionChanged(action);
  }

  setOnMeasurerSpaceBetweenLinesChanged(action) {
    this.offlinePlotObjects.setOnMeasurerSpaceBetweenLinesChanged(action);
  }

  convertMsToPixels(ms) {
    return this.offlinePlotObjects.convertMsToPixels(ms);
  }

  convertPixelsToMs(pixels) {
    return this.offlinePlotObjects.convertPixelsToMs(pixels);
  }

  getSignalWidthInPixels() {
    return this.offlinePlotObjects.getSignalWidthInPixels();
  }

  getSignalWidthInMs() {
    return this.offlinePlotObjects.getSignalWidthInMs();
  }

  getSpaceBetweenLines() {
    return this.offlinePlotObjects.getSpaceBetweenLines();
  }

  setSpaceBetweenLines(widthInPixels) {
    this.offlinePlotObjects.setSpaceBetweenLines(widthInPixels);
  }

  changeMeasurerMode() {
    this.offlinePlotObjects.changeMeasurerMode();
  }

  getKeybordEventHanlder() {
    return this.offlinePlotObjects.getKeybordEventHanlder();
  }

  _onSignalBoxBufferSizeChanged() {
    this.onlinePlotObjects.clearEventObjects();
    this.clearSignalDependentMarks();
  }

  afterAddPointsToChannels(sourceId, channelId, blockSize, frameCounter) {
    if (this.signalMode == "offline") {
      return;
    }
    this.onlinePlotObjects.updateEventObjects(sourceId, channelId, blockSize, frameCounter);
  }

  // points in user units (e.g. mV) or in pixels (if valueConverter is not set)
  afterAddPointsToSignal(dataSourceId, channelId /*, signalId*/) {
    if (this.signalMode == "online") {
      this.onlinePlotObjects.updateLeadRaysPosition(dataSourceId, channelId);
    } else {
      this.offlinePlotObjects.updateCursorAndMeasurerPosition();
    }
  }

  afterChannelsDataClear() {
    if (this.signalMode == "online") {
      this.onlinePlotObjects.setLeadRaysToZero();
      this.onlinePlotObjects.clearEventObjects();
    }
  }

  changeSignalMode(mode) {
    if ((mode != "online" && mode != "offline") || this.signalMode == mode) {
      return;
    }
    this.signalMode = mode;
    switch (this.signalMode) {
      case "online":
        this._setOnlineMode();
        break;
      case "offline":
        this._setOfflineMode();
        break;
    }
    this.onSignalModeChanged?.(mode);
  }

  setScaleX(newScaleX) {
    super.setScaleX(newScaleX);
    if (this.signalMode == "offline") {
      this._loadDataFromSource();
    }
    this.settings.canvas.refreshGraphics();
  }

  addLineMark(data) {
    if (this.signalMode == "online") {
      this.onlinePlotObjects.addMark(data);
    }
  }

  setFilters(selectedFilterNames) {
    super.setFilters(selectedFilterNames);
    if (this.signalMode == "offline") {
      this.offlinePlotObjects.updateMeanValues();
    }
  }

  setChannelFilters(dataSourceId, channelId, selectedFilterNames) {
    super.setChannelFilters(dataSourceId, channelId, selectedFilterNames);
    if (this.signalMode == "offline") {
      this.offlinePlotObjects.updateMeanValues();
    }
  }

  clearChannelData(dataSourceId, channelId) {
    this.forEachChannelSignal(dataSourceId, channelId, signalObject => {
      signalObject.resetBuffer();
      signalObject.resetSourceBuffer();
    });
    if (this.afterChannelDataClear !== undefined) {
      this.afterChannelDataClear(dataSourceId, channelId);
    }
  }

  afterChannelDataUpdated() {}

  createBorders() {
    super.createBorders();
    if (this.signalMode == "offline") {
      this._loadDataFromSource();
    }
  }

  _setOnlineMode() {
    this.forEachSignal(signalObject => {
      signalObject.resetBuffer();
      signalObject.resetSourceBuffer();
    });
    this.onlinePlotObjects.showLeadRays();
    this.offlinePlotObjects.cursor.setVisibility(false);
    this.offlinePlotObjects.measurer.setVisibility(false);
  }

  _setOfflineMode() {
    this.onlinePlotObjects.hideLeadRays();
    this.offlinePlotObjects.updateMeasurerMode();
  }

  _loadDataFromSource() {
    this.forEachSignal(signalObject => {
      signalObject.loadDataFromSource();
      const signalSettings = signalObject.settings.signalSettings;
      this.afterAddPointsToSignal(signalSettings.id_dataSource, signalSettings.id_channel, signalSettings.id);
    });
  }
}
